import { Card, Form } from "@rothoblaas/design-system";
import { FC, useContext, useEffect, useState } from "react";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { UpdateUserBody, User } from "../../http/types/users";
import { useUsersApi } from "../../http/useUsers";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import RolesPermissionsFields from "./RolesPermissionsFields";

interface PermissionsFormBody {
  roles: string[];
  permissions: string[];
}

const AdminUserPermissions: FC<{ user?: User; disabled?: boolean }> = ({ user, disabled }) => {
  const auth = useAuthContext();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const usersApi = useUsersApi();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValues: PermissionsFormBody = {
    roles: user?.roles ?? [],
    permissions: user?.permissions ?? [],
  };

  const onSubmit = () => (formData: PermissionsFormBody) => {
    if (!user?.id) {
      return;
    }

    setIsSubmitting(true);
    const body: UpdateUserBody = {
      roles: formData.roles ?? [],
      permissions: formData.permissions ?? [],
    };

    usersApi
      .updateUser(user.id, body)
      .then(() => dispatch(showSuccessNotification(i18n.translation)))
      .catch((err) => dispatch(showErrorNotification(i18n, err)))
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Card shadow>
      <Form<PermissionsFormBody>
        submitText={i18n.translation.common.save}
        disabled={disabled || isSubmitting}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
      >
        {({ setValue, reset, watch }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [user]);

          const roles = watch("roles");
          const permissions = watch("permissions");

          return (
            <FormFieldsContainer fullWidth>
              <RolesPermissionsFields
                roles={roles}
                permissions={permissions}
                onRolesChange={(value) => setValue("roles", value)}
                onPermissionsChange={(value) => setValue("permissions", value)}
                disabled={disabled || user?.id === auth.identity?.id}
              />
            </FormFieldsContainer>
          );
        }}
      </Form>
    </Card>
  );
};

export default AdminUserPermissions;
